import { SetStateAction, useEffect, useState } from 'react';
import {
  ComparisonPeriod,
  getAdminBenchmarkStatistics,
  getProvincesListForStatistics,
  getRegionsListForStatistics,
  Period,
} from '../../../services/api/statistics/statistics';
import { useQuery } from '@tanstack/react-query';
import {
  getDashboardFiltersInSessionStorage,
  setDashboardFiltersInSessionStorage,
} from '../../../services/sessionStorage/sessionStorage';

interface DateRange {
  start: Date | null;
  end: Date | null;
}

const useAdminStatistics = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<Period>('last_3_months');
  const [selectedComparisonPeriod, setSelectedComparisonPeriod] =
    useState<ComparisonPeriod>('previous_period');

  // Singolo stato per il date range
  const [dateRange, setDateRange] = useState<DateRange>({
    start: null,
    end: null,
  });

  const [selectedRegion, setSelectedRegion] = useState<string | null>('');
  const [selectedProvince, setSelectedProvince] = useState<string | null>('');

  // Determina se stiamo usando un range personalizzato
  const isUsingCustomDateRange = dateRange.start && dateRange.end;

  // Query per le statistiche benchmark
  const {
    data: benchmarkStatistics,
    isPending,
    error,
  } = useQuery({
    queryKey: [
      'benchmarkStatistics',
      selectedPeriod,
      dateRange.start,
      dateRange.end,
      selectedRegion,
      selectedProvince,
      selectedComparisonPeriod,
    ],
    queryFn: () => {
      // Stessa logica per le statistiche benchmark
      if (isUsingCustomDateRange && dateRange.start && dateRange.end) {
        return getAdminBenchmarkStatistics(
          undefined,
          dateRange.start,
          dateRange.end,
          selectedRegion,
          selectedProvince,
          selectedComparisonPeriod
        );
      }
      return getAdminBenchmarkStatistics(
        selectedPeriod,
        undefined,
        undefined,
        selectedRegion,
        selectedProvince,
        selectedComparisonPeriod
      );
    },
    enabled: !!(selectedPeriod || isUsingCustomDateRange),
  });

  // Query popolare la select delle regioni
  const {
    data: regions,
    /*    isPending: isRegionsPending,
    error: regionsError,*/
  } = useQuery({
    queryKey: ['regionsForStatistics'],
    queryFn: () => {
      return getRegionsListForStatistics();
    },
  });

  // Query popolare la select delle regioni
  const {
    data: provinces,
    /*    isPending: isRegionsPending,
    error: regionsError,*/
  } = useQuery({
    queryKey: ['provincesForStatistics', selectedRegion],
    queryFn: () => {
      return getProvincesListForStatistics(selectedRegion);
    },
  });

  useEffect(() => {
    const filters = getDashboardFiltersInSessionStorage();
    if (filters) {
      if (filters.selectedPeriod) {
        setSelectedPeriod(filters.selectedPeriod);
      }
      if (filters.dateRange?.start && filters.dateRange?.end) {
        setDateRange({
          start: new Date(filters.dateRange.start),
          end: new Date(filters.dateRange.end),
        });
      }
    }
  }, []);

  const handleSelectPeriod = (period: Period) => {
    setSelectedPeriod(period);
    setDashboardFiltersInSessionStorage({ selectedPeriod: period });
  };

  const handleDateRangeChange = (value: SetStateAction<DateRange>) => {
    setDateRange((prevValue) => {
      // Calcola il nuovo valore
      const newValue = typeof value === 'function' ? value(prevValue) : value;

      setDashboardFiltersInSessionStorage({ dateRange: newValue });

      return newValue;
    });
  };

  const getFilterText = () => {
    const filters = [];

    if (selectedPeriod && !dateRange?.start) {
      filters.push(`Periodo: ${selectedPeriod}`);
    }
    if (dateRange?.start && dateRange?.end) {
      filters.push(
        `Periodo: dal ${dateRange.start.toLocaleDateString(
          'it-IT'
        )} al ${dateRange.end.toLocaleDateString('it-IT')}`
      );
    }
    if (selectedRegion) {
      filters.push(`Regione: ${selectedRegion}`);
    }
    if (selectedProvince) {
      filters.push(`Provincia: ${selectedProvince}`);
    }

    return filters.join(' | ');
  };

  return {
    isPending,
    error,
    selectedPeriod,
    setSelectedPeriod: handleSelectPeriod,
    selectedComparisonPeriod,
    setSelectedComparisonPeriod,
    benchmarkStatistics,
    dateRange,
    setDateRange: handleDateRangeChange,
    isUsingCustomDateRange,
    regionsFilterOptions: regions || [],
    selectedRegion,
    setSelectedRegion,
    provincesFilterOptions: provinces || [],
    selectedProvince,
    setSelectedProvince,
    getFilterText,
  };
};

export default useAdminStatistics;
